import * as Api from '@ViewModels';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { ADMIN_ACCOUNT_QUERY_KEY } from './useAdminAccountQuery';

export interface IUpdateAdminAccountRequest {
	account: Api.IAccount;
}

/**
 * Thrown when the account PUT succeeded but the follow-up Comp `mainAccountId` PATCH did not, so the
 * account is partially saved. The two calls cannot be made atomic from the client, so this surfaces
 * the split precisely instead of letting a generic "nothing saved" message mislead the admin into
 * thinking their edits were lost. `savedAccount` is the (already-persisted) PUT result.
 */
export class PartialAccountSaveError extends Error {
	public readonly savedAccount: Api.IAccount | null;

	constructor(savedAccount: Api.IAccount | null) {
		super('Account details saved, but linking the main account failed. Re-check the main account and try again.');
		this.name = 'PartialAccountSaveError';
		this.savedAccount = savedAccount;
	}
}

/**
 * Saves a full admin account — the react-query replacement for the MobX
 * `AccountOperationsViewModel.saveAccount` / `.saveMainAccountId` path.
 *
 * Behavior mirrored from the legacy view model so it is a drop-in for the save:
 * - PUTs `admin/account/{id}` with the server-computed `health` stripped from
 *   `additionalInfo` (the server owns that field).
 * - For Comp accounts, follows the PUT with a `mainAccountId` PATCH; that PATCH
 *   result becomes the saved account (matching the legacy two-call save). If only the
 *   PATCH fails, rejects with {@link PartialAccountSaveError} so the caller can say
 *   which half persisted rather than reporting a total failure.
 * - Writes the saved account back into the account query cache so the page updates
 *   without a refetch (same `setQueryData` the V2 page does today).
 *
 * No impersonation context is applied — the endpoint is scoped by the `{accountId}`
 * path segment plus the admin's bearer token.
 *
 * NOTE: there is no global `MutationCache`, so consumers MUST pass an explicit
 * `onError` (e.g. a toast). Errors are otherwise surfaced only via the rejected
 * `mutateAsync` promise. Date normalization (noon-ISO for `adsKickoffDate` /
 * `lastAdsContact`) and the Chargify `billingReferenceId` parse are applied by the
 * caller before invoking this mutation, exactly as the legacy component did.
 */
export function useUpdateAdminAccountMutation() {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ account }: IUpdateAdminAccountRequest) => {
			// Strip server-computed health; the server rejects/ignores it on write.
			const { health: _health, ...additionalInfoWithoutHealth } = account.additionalInfo || {};
			const payload = { ...account, additionalInfo: additionalInfoWithoutHealth };

			const saved = await userSession.webServiceHelper.callAsync<Api.IAccount>(
				`admin/account/${account.id}`,
				'PUT',
				payload
			);

			// Comp accounts keep a main-account link. Only PATCH when a mainAccountId is actually
			// set — sending an empty value can be read by the API as "unlink" and silently clear it.
			// The pair is not transactional and cannot be made so from here, so a PATCH failure is
			// reported as a PartialAccountSaveError: the PUT above has already persisted.
			if (account.planDetails?.billingType === Api.BillingType.Comp && account.mainAccountId) {
				try {
					const savedWithMainAccount = await userSession.webServiceHelper.callAsync<Api.IAccount>(
						`admin/account/${account.id}/mainAccountId/?mainAccountId=${encodeURIComponent(account.mainAccountId)}`,
						'PATCH'
					);
					return savedWithMainAccount ?? saved;
				} catch {
					throw new PartialAccountSaveError(saved ?? null);
				}
			}

			return saved;
		},
		onSuccess: savedAccount => {
			if (savedAccount?.id) {
				queryClient.setQueryData([ADMIN_ACCOUNT_QUERY_KEY, savedAccount.id], savedAccount);
			}
		},
		onError: error => {
			// A partial save still persisted the PUT, so cache that result — otherwise the read view
			// keeps showing pre-save values for changes that are actually live on the server.
			if (error instanceof PartialAccountSaveError && error.savedAccount?.id) {
				queryClient.setQueryData([ADMIN_ACCOUNT_QUERY_KEY, error.savedAccount.id], error.savedAccount);
			}
		},
	});
}
